<template>
  <BcSheet :step="step"
           :total="5"
           :show-back="showBack"
           :locked="sending"
           :dismiss-back="dismissBack"
           :title="title"
           :why="why"
           @back="onBack"
           @close="onClose">
    <!-- 2 · กลุ่มไหน? (also the เปลี่ยน sub-view of step 5) -->
    <div v-if="sub === 'who' || step === 2">
      <BcChoiceButton v-for="g in groupList"
                      :key="g.key"
                      :label="g.name"
                      :description="g.sub"
                      :count="g.count"
                      :selected="group === g.key"
                      @click="pickGroup(g.key)" />
    </div>

    <!-- 1 · ส่งถึงใคร? -->
    <div v-else-if="step === 1">
      <BcChoiceButton :label="$t('broadcast.screens.scope.day')"
                      :selected="scope === 'day'"
                      @click="pickScope('day')" />
      <BcChoiceButton :label="$t('broadcast.screens.scope.inbox')"
                      :selected="scope === 'inbox'"
                      @click="pickScope('inbox')" />
    </div>

    <!-- 3 · ส่งจากเพจไหน? -->
    <PagePickerSheet v-else-if="step === 3"
                     embedded
                     :pages="pages"
                     :value="selected"
                     @input="selected = $event"
                     @reconnect="$emit('reconnect', $event)" />

    <!-- 4 · whole inbox: ส่งข้อความแบบไหน? -->
    <div v-else-if="step === 4 && scope === 'inbox'">
      <BcChoiceButton v-for="t in itypes"
                      :key="t.key"
                      :label="$t('broadcast.screens.itype.' + t.key)"
                      :selected="itype === t.key"
                      :disabled="t.key === 'after' && !postSaleGroup"
                      @click="pickItype(t.key)" />
      <div v-if="itype"
           class="bcx-pane mt-3">
        <BcBubble :parts="inboxParts" />
      </div>
      <div v-if="itype && itype !== 'own' && sellerSlots.length"
           class="mt-2">
        <div v-for="slot in sellerSlots"
             :key="slot.key">
          <span class="bcx-label">{{ slot.labelTh || slot.key }}</span>
          <input v-model="slots[slot.key]"
                 class="form-control mb-2"
                 :aria-label="slot.labelTh || slot.key"
                 autocomplete="off" />
        </div>
      </div>
      <div v-if="itype === 'own'"
           class="mt-2">
        <p class="bcx-own-warn">{{ $t("broadcast.screens.itype.own_warn") }}</p>
        <textarea v-model="ownText"
                  class="form-control mb-1"
                  rows="4"
                  maxlength="2000"
                  :aria-label="$t('broadcast.screens.itype.own')"></textarea>
        <div class="d-flex justify-content-between align-items-center mb-2">
          <small v-if="ownText.length >= 2000"
                 class="text-warning font-weight-bold">
            <i class="fal fa-exclamation-triangle mr-1"></i>{{ $t("broadcast.screens.itype.too_long") }}
          </small>
          <span v-else></span>
          <div class="bcx-count"
               :class="{ 'text-warning font-weight-bold': ownText.length >= 2000 }">({{ ownText.length }}/2000)</div>
        </div>
        <div v-for="key in ownParams"
             :key="key">
          <span class="bcx-label">{{ key }}</span>
          <input v-model="slots[key]"
                 class="form-control mb-2"
                 :aria-label="key"
                 autocomplete="off" />
        </div>
      </div>
    </div>

    <!-- 4 · 24 h: free composer -->
    <div v-else-if="step === 4">
      <button type="button"
              class="bcx-chip"
              @click="putName">
        {{ $t("broadcast.screens.compose.put") }} <span class="t">{{ nameChip }}</span>
      </button>
      <textarea ref="text"
                v-model="msg.text"
                class="form-control mt-2"
                rows="4"
                maxlength="2000"
                :placeholder="$t('broadcast.screens.compose.placeholder')"
                @input="msg.touched = true"></textarea>
      <div class="d-flex justify-content-between align-items-center mt-1">
        <small v-if="msg.text.length >= 2000"
               class="text-warning font-weight-bold">
          <i class="fal fa-exclamation-triangle mr-1"></i>{{ $t("broadcast.screens.compose.too_long") }}
        </small>
        <span v-else></span>
        <div class="bcx-count"
             :class="{ 'text-warning font-weight-bold': msg.text.length >= 2000 }">({{ msg.text.length }}/2000)</div>
      </div>
      <div class="bcx-rowbtns mt-2">
        <button type="button"
                class="btn"
                :class="msg.imageUrl ? 'btn-outline-primary' : 'btn-default'"
                :disabled="uploading"
                @click="toggleImage">{{ msg.imageUrl ? $t("broadcast.screens.compose.remove_image") : $t("broadcast.screens.compose.add_image") }}</button>
        <input ref="file"
               type="file"
               accept="image/*"
               class="d-none"
               @change="onFile" />
      </div>
      <div class="bcx-pane mt-3">
        <div class="bcx-pane-head">{{ $t("broadcast.screens.compose.preview") }}</div>
        <BcBubble :parts="composeParts"
                  :image-url="msg.imageUrl"
                  :empty="$t('broadcast.screens.compose.type_above')" />
      </div>
    </div>

    <!-- 5 · ส่งเลยไหม? -->
    <div v-else>
      <div class="bcx-summary">
        <p v-if="draft"
           class="bcx-bignum">{{ $t("broadcast.screens.confirm.send_to", { n: recipients.toLocaleString("en-US") }) }}</p>
        <p v-else-if="creating"
           class="bcx-bignum"><i class="fal fa-spinner-third fa-spin"></i></p>
        <p class="bcx-line">
          <span>{{ groupName(group) }}</span>
          <button type="button"
                  class="btn btn-outline-primary btn-sm"
                  :disabled="sending"
                  @click="openWho">{{ $t("broadcast.screens.confirm.change") }}</button>
        </p>
        <p class="bcx-line bcx-muted">
          <span>{{ $t("broadcast.screens.home.from_pages", { n: selectedCount.toLocaleString("en-US") }) }}</span>
          <button type="button"
                  class="btn btn-outline-primary btn-sm"
                  :disabled="sending"
                  @click="openPages">{{ $t("broadcast.screens.confirm.change") }}</button>
        </p>
        <div class="my-3">
          <BcBubble :parts="confirmParts"
                    :image-url="confirmImage" />
        </div>
        <p v-if="scope === 'inbox' && waitingForMeta"
           class="bcx-pending">{{ $t("broadcast.screens.confirm.pending") }}</p>
        <p v-else-if="itype === 'own' && scope === 'inbox' && tplState === 'approved'"
           class="bcx-approved">{{ $t("broadcast.screens.confirm.approved") }}</p>
        <p v-if="draft && draft.audience.lines && draft.audience.lines.syncTh"
           class="bcx-note">{{ draft.audience.lines.syncTh }}</p>
        <p v-if="draft && draft.audience.lines && draft.audience.lines.templateTh"
           class="bcx-note">{{ draft.audience.lines.templateTh }}</p>
      </div>
      <p v-if="draft && recipients === 0"
         class="bcx-rejected">{{ $t("broadcast.screens.confirm.none") }}</p>
      <div v-if="createFailed">
        <p v-if="createError || !refused"
           class="bcx-rejected">{{ createError || $t("messenger.unavailable") }}</p>
        <button v-if="refused && itype === 'own'"
                type="button"
                class="btn btn-primary btn-lg mb-2"
                @click="to24h">{{ $t("broadcast.screens.confirm.to_24h") }}</button>
        <button v-if="!createError && !refused"
                type="button"
                class="btn btn-outline-primary mb-2"
                @click="prepare">{{ $t("messenger.retry") }}</button>
      </div>
      <div v-if="testOpen"
           class="mb-2">
        <input v-model="phone"
               class="form-control"
               inputmode="tel"
               autocomplete="off"
               :placeholder="$t('broadcast.screens.confirm.test_phone')"
               :aria-label="$t('broadcast.screens.confirm.test')"
               @keyup.enter="sendTest" />
        <div class="bcx-note">{{ $t("broadcast.screens.confirm.test_hint") }}</div>
      </div>
    </div>

    <template v-if="hasFoot"
              #foot>
      <template v-if="sub === 'who'">
        <button type="button"
                class="btn btn-primary btn-lg"
                :disabled="!group"
                @click="whoOk">{{ $t("broadcast.screens.group.ok") }}</button>
      </template>
      <template v-else-if="step === 3">
        <span class="bcx-sticky-n">{{ $t("broadcast.pages.selected", { n: selectedCount.toLocaleString("en-US") }) }}</span>
        <button type="button"
                class="btn btn-primary btn-lg"
                :disabled="!selectedCount"
                @click="pagesNext">{{ $t("broadcast.screens.next") }}</button>
      </template>
      <template v-else-if="step === 4">
        <button type="button"
                class="btn btn-primary btn-lg"
                :disabled="!stepValid"
                @click="goConfirm">{{ $t("broadcast.screens.next") }}</button>
      </template>
      <template v-else-if="step === 5">
        <button type="button"
                class="btn btn-default"
                :disabled="!draft || sending || testing"
                @click="sendTest">{{ $t("broadcast.screens.confirm.test") }}</button>
        <button v-if="waitingForMeta"
                type="button"
                class="btn btn-primary btn-lg"
                disabled>{{ $t("broadcast.screens.confirm.wait") }}</button>
        <button v-else
                type="button"
                class="btn btn-primary btn-lg"
                :disabled="!canSend"
                @click="send">
          <i v-if="sending"
             class="fal fa-spinner-third fa-spin mr-1"></i>{{ sending ? $t("broadcast.screens.confirm.sending") : $t("broadcast.screens.confirm.send") }}
        </button>
      </template>
    </template>
  </BcSheet>
</template>

<script>
import BcSheet from "@/components/derived/broadcast/BcSheet";
import BcChoiceButton from "@/components/derived/broadcast/BcChoiceButton";
import BcBubble from "@/components/derived/broadcast/BcBubble";
import PagePickerSheet from "@/components/derived/broadcast/PagePickerSheet";
import { bcToast } from "@/components/derived/broadcast/toast";
import { http } from "@/helpers/http";
import { GROUPS, POST_SALE_GROUPS, ITYPES, PREFILL, NAME_CHIP, NAME_TOKEN, SAMPLE, newKey, splitMarks } from "@/components/derived/broadcast/copy";

// The same upload endpoint components/core/ImageUpload.vue posts to (multipart "file" -> {urls: [..]})
const MEDIA_URL = "https://8enalpa7e5.execute-api.ap-southeast-1.amazonaws.com/dev/v1/media";
const OWN_PARAM = /\{\{([a-z][a-z0-9_]*)\}\}/g;
const POLL_MS = 5000;

const dispatch = (vm, action, payload) =>
  new Promise((resolve) => {
    vm.$store.dispatch("broadcast/" + action, Object.assign({}, payload, { callback: (ok, data) => resolve({ ok, data }) }));
  });

// The 5-step popup: ส่งถึงใคร? > กลุ่มไหน? > ส่งจากเพจไหน? > the message > ส่งเลยไหม?
// Props: `pages` (GET /api/broadcast/pages), `resume` (the library hook: {itype, template}) or null.
// Emits: close, sent(id) once the send started, reconnect(page).
// A fresh component per opening = the mockup's reset on reopen; the parent renders it with v-if.
export default {
  name: "BcWizard",
  components: { BcSheet, BcChoiceButton, BcBubble, PagePickerSheet },
  props: {
    pages: { type: Array, required: true },
    resume: { type: Object, default: null },
  },

  data() {
    return {
      step: 1,
      sub: null, // "who": the เปลี่ยน group sub-view of step 5
      ret: null, // step to return to after changing Pages from step 5
      snap: null,
      scope: null, // day | inbox
      group: null,
      selected: [],
      msg: { text: "", imageUrl: "", touched: false },
      uploading: false,
      itype: null,
      slots: {},
      ownText: "",
      ownTpl: null, // the seller's own template once created / chosen from the library
      ownFromLibrary: null,
      templates: null,
      counts: null,
      draft: null, // {id, sig, audience, samples}
      creating: false,
      createError: "",
      createFailed: false,
      refused: false, // เฟส said no: nothing to retry
      templateWait: false, // เฟส has not approved the message on every selected Page yet
      retryTimer: null,
      sending: false,
      phone: "",
      testOpen: false,
      testing: false,
      seq: 0, // newest draft request wins
      countSeq: 0,
      keys: {}, // idempotency key per draft signature
      poll: null,
      itypes: ITYPES,
    };
  },

  computed: {
    eligible() {
      return this.pages.filter((p) => p.eligible);
    },
    eligibleIds() {
      return this.eligible.map((p) => String(p.pageId));
    },
    selectedPages() {
      const ids = new Set(this.selected.map(String));
      return this.eligible.filter((p) => ids.has(String(p.pageId)));
    },
    selectedCount() {
      return this.selectedPages.length;
    },
    nameChip() {
      return NAME_CHIP;
    },
    postSaleGroup() {
      return POST_SALE_GROUPS.includes(this.group);
    },
    showBack() {
      return !!this.sub || this.step > 1;
    },
    dismissBack() {
      return !!this.sub || (this.ret != null && this.step === 3);
    },
    hasFoot() {
      return this.sub === "who" || this.step >= 3;
    },

    title() {
      const s = this.$t.bind(this);
      if (this.sub === "who" || this.step === 2) return s("broadcast.screens.group.title");
      if (this.step === 1) return s("broadcast.screens.scope.title");
      if (this.step === 3) return s("broadcast.pages.title");
      if (this.step === 4) return s(this.scope === "inbox" ? "broadcast.screens.itype.title" : "broadcast.screens.compose.title");
      return s("broadcast.screens.confirm.title");
    },
    why() {
      if (this.sub) return "";
      if (this.step === 1) return this.$t("broadcast.screens.scope.why");
      if (this.step === 4 && this.scope !== "inbox") return this.$t("broadcast.screens.compose.why");
      if (this.step === 5 && this.scope !== "inbox") return this.$t("broadcast.screens.confirm.why");
      return "";
    },

    // ---- step 2
    groupList() {
      return GROUPS.map((key) => ({
        key,
        name: this.groupName(key),
        sub: this.$te("broadcast.screens.group." + key + "_sub") ? this.$t("broadcast.screens.group." + key + "_sub") : "",
        count: this.groupCount(key),
      }));
    },

    // ---- step 4 (whole inbox)
    currentTpl() {
      if (this.itype === "own") return this.ownTpl;
      const def = ITYPES.find((t) => t.key === this.itype);
      return (def && this.templates && this.templates.find((t) => t.key === def.templateKey)) || null;
    },
    ownParams() {
      const found = [];
      const text = this.ownText;
      let m;
      OWN_PARAM.lastIndex = 0;
      while ((m = OWN_PARAM.exec(text))) if (!["name", "shop"].includes(m[1]) && !found.includes(m[1])) found.push(m[1]);
      return found;
    },
    sellerSlots() {
      if (this.itype === "own") return this.ownParams.map((key) => ({ key, filledBy: "seller", labelTh: key }));
      return ((this.currentTpl && this.currentTpl.slots) || []).filter((s) => s.filledBy === "seller");
    },
    shopName() {
      const p = this.selectedPages[0];
      return p ? (p.shop && p.shop.name) || p.name : "";
    },
    inboxParts() {
      if (!this.itype) return [];
      if (this.itype === "own") return this.bodyParts(this.ownText, []);
      return this.currentTpl ? this.bodyParts(this.currentTpl.body, this.currentTpl.slots || []) : [];
    },
    inboxValid() {
      if (!this.itype) return false;
      if (this.itype === "own") return !!this.ownText.trim() && this.ownText.length <= 2000 && this.sellerSlots.every((s) => String(this.slots[s.key] || "").trim());
      if (!this.currentTpl) return false;
      if (this.itype === "after") return true;
      return this.sellerSlots.every((s) => String(this.slots[s.key] || "").trim());
    },
    stepValid() {
      if (this.scope === "inbox") return this.inboxValid;
      return !!this.msg.text.trim() && this.msg.text.length <= 2000 && !this.uploading;
    },
    composeParts() {
      return splitMarks(this.msg.text, [{ token: NAME_CHIP, show: SAMPLE.name, kind: "name" }]);
    },

    // ---- step 5
    recipients() {
      return this.draft ? Number(this.draft.audience.recipients) || 0 : 0;
    },
    confirmParts() {
      const sample = this.draft && this.draft.samples && this.draft.samples[0];
      if (sample && sample.text) return splitMarks(sample.text, [{ token: sample.buyerName, kind: "name" }]);
      return this.scope === "inbox" ? this.inboxParts : this.composeParts;
    },
    confirmImage() {
      const sample = this.draft && this.draft.samples && this.draft.samples[0];
      if (sample) return sample.imageUrl || "";
      return this.scope === "inbox" ? "" : this.msg.imageUrl;
    },
    // เฟส's answer for the chosen message over the selected Pages (a Page it is not registered on counts as waiting)
    tplState() {
      const tpl = this.currentTpl;
      if (!tpl) return "pending";
      const rows = this.selectedPages.map((p) => (tpl.pages || []).find((r) => String(r.pageId) === String(p.pageId)));
      if (rows.some((r) => r && r.status === "rejected")) return "rejected";
      return rows.every((r) => r && r.status === "approved") ? "approved" : "pending";
    },
    rejectText() {
      const tpl = this.currentTpl;
      const row = tpl && (tpl.pages || []).find((r) => r.status === "rejected" && r.rejectTh);
      return (row && row.rejectTh) || (tpl && tpl.messageTh) || "";
    },
    waitingForMeta() {
      return this.scope === "inbox" && this.templateWait;
    },
    canSend() {
      return !!this.draft && this.recipients > 0 && !this.creating && !this.sending && !this.createFailed;
    },
    selectedIds() {
      return this.selectedPages.map((p) => String(p.pageId));
    },
    confirmKey() {
      if (this.step !== 5 || this.sub || !this.group) return "";
      return JSON.stringify([this.scope, this.group, this.selected.slice().sort(), this.msg.text, this.msg.imageUrl, this.itype, this.slots, this.ownText]);
    },
  },

  watch: {
    confirmKey(key) {
      if (key) this.prepare();
      else this.stopPoll();
    },
    step(step) {
      if (step === 4) {
        this.applyPrefill();
        if (this.scope === "inbox") this.loadTemplates();
      }
      this.$nextTick(() => {
        const main = this.$el && this.$el.querySelector && this.$el.querySelector(".bc-sheet-main");
        if (main) main.scrollTop = 0;
      });
    },
  },

  created() {
    this.selected = this.eligibleIds.slice(); // default: every eligible Page
    if (this.resume) this.applyResume(this.resume);
  },

  beforeDestroy() {
    this.stopPoll();
    clearTimeout(this.retryTimer);
    this.seq += 1;
  },

  methods: {
    groupName(key) {
      return this.$t("broadcast.screens.group." + key);
    },

    groupCount(key) {
      const c = this.counts;
      return c && c[key] != null ? Number(c[key]) : null;
    },

    // Step 2's figures for the selected Pages; asked again when the kind or the Pages change
    async loadCounts() {
      if (!this.scope || !this.selectedIds.length) return;
      const seq = ++this.countSeq;
      const { ok, data } = await dispatch(this, "fetchCounts", {
        kind: this.scope === "day" ? "window" : "utility",
        pageIds: this.selectedIds,
      });
      if (this._isDestroyed || seq !== this.countSeq) return;
      this.counts = ok && data ? data.counts || null : null;
    },

    async loadTemplates() {
      const { ok, data } = await dispatch(this, "fetchTemplates", {});
      if (ok) this.templates = data.templates || [];
      return ok;
    },

    // The library's ใช้ข้อความนี้: popup at step 4, สั่งแล้ว, every Page, the chosen message selected.
    // An own message that is not on every Page sends the seller through the picker first.
    async applyResume(resume) {
      this.scope = "inbox";
      this.group = "ordered";
      this.itype = resume.itype;
      let step = 4;
      if (resume.template && resume.itype === "own") {
        this.ownFromLibrary = resume.template;
        this.ownText = resume.template.body;
        const onIds = (resume.template.pages || []).map((p) => String(p.pageId));
        if (!this.eligibleIds.every((id) => onIds.includes(id))) step = 3;
      }
      this.step = step;
      this.loadCounts();
      await this.loadTemplates();
    },

    // ---- navigation
    pickScope(scope) {
      if (this.scope !== scope) {
        this.scope = scope;
        this.group = null;
        this.itype = null;
        this.slots = {};
        this.ownText = "";
        this.ownTpl = null;
        this.ownFromLibrary = null;
        this.draft = null;
        this.counts = null;
      }
      this.step = 2;
      this.loadCounts();
    },

    // เฟส refused the own wording: carry the text over to the 24 h reach and go on from the group step
    to24h() {
      // the 24 h composer only knows {{ชื่อ}}: fill the other tokens now, pickScope wipes the slots
      const text = this.ownText
        .replace(OWN_PARAM, (_, k) => (k === "name" ? NAME_CHIP : k === "shop" ? this.shopName : String(this.slots[k] || "").trim()))
        .replace(/ {2,}/g, " ");
      this.seq += 1; // drop any draft request still in flight
      clearTimeout(this.retryTimer);
      this.stopPoll();
      this.createError = "";
      this.createFailed = false;
      this.refused = false;
      this.templateWait = false;
      this.pickScope("day"); // the same reset as changing reach on step 1
      if (text.trim()) {
        this.msg.text = text;
        this.msg.touched = true;
      }
      this.msg.imageUrl = "";
    },

    pickGroup(key) {
      this.group = key;
      if (this.itype === "after" && !POST_SALE_GROUPS.includes(key)) this.itype = null;
      if (!this.sub) this.step = 3; // step 2: a tap advances; in the sub-view it only selects until ตกลง
    },

    pickItype(key) {
      if (this.itype === key) return;
      this.itype = key;
      this.slots = {};
    },

    openWho() {
      this.snap = { group: this.group, itype: this.itype };
      this.sub = "who";
    },

    whoOk() {
      if (!this.group) return;
      this.snap = null;
      this.sub = null;
      if (this.scope === "inbox" && !this.inboxValid) this.step = 4;
      else this.applyPrefill();
    },

    openPages() {
      this.snap = { selected: this.selected.slice() };
      this.ret = 5;
      this.step = 3;
    },

    pagesNext() {
      if (!this.selectedCount) return;
      this.step = this.ret || 4;
      this.ret = null;
      this.snap = null;
      this.loadCounts();
    },

    goConfirm() {
      if (!this.stepValid) return;
      this.ret = null;
      this.step = 5;
    },

    restore() {
      if (this.snap) {
        if (this.snap.group !== undefined) this.group = this.snap.group;
        if (this.snap.itype !== undefined) this.itype = this.snap.itype;
        if (this.snap.selected) this.selected = this.snap.selected.slice();
      }
      this.snap = null;
      this.sub = null;
      if (this.step === 5 && !this.group) this.step = 2; // no group picked yet
    },

    stepBack() {
      if (this.sub) {
        this.restore();
        return true;
      }
      if (this.ret != null && this.step === 3) {
        this.restore();
        this.step = this.ret;
        this.ret = null;
        return true;
      }
      if (this.step > 1) {
        this.step -= 1;
        return true;
      }
      return false;
    },

    onBack() {
      if (this.sending) return;
      if (!this.stepBack()) this.$emit("close");
    },

    onClose() {
      if (this.sending) return;
      this.$emit("close");
    },

    // ---- 24 h composer
    applyPrefill() {
      if (this.scope !== "day" || !PREFILL[this.group]) return;
      const m = this.msg;
      if (!m.touched || Object.keys(PREFILL).some((k) => PREFILL[k] === m.text)) m.text = PREFILL[this.group];
    },

    putName() {
      const field = this.$refs.text;
      const text = this.msg.text;
      const start = field && field.selectionStart != null ? field.selectionStart : text.length;
      const end = field && field.selectionEnd != null ? field.selectionEnd : text.length;
      this.msg.text = text.slice(0, start) + NAME_CHIP + text.slice(end);
      this.msg.touched = true;
      this.$nextTick(() => {
        if (!field) return;
        field.focus();
        const at = start + NAME_CHIP.length;
        field.setSelectionRange(at, at);
      });
    },

    toggleImage() {
      if (this.msg.imageUrl) this.msg.imageUrl = "";
      else if (this.$refs.file) this.$refs.file.click();
    },

    async onFile(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = "";
      if (!file) return;
      this.uploading = true;
      try {
        const form = new FormData();
        form.append("file", file);
        const response = await http.post(MEDIA_URL, form);
        const url = response && response.data && response.data.urls && response.data.urls[0];
        if (url) this.msg.imageUrl = url;
      } catch (e) {
        // the shared http helper already toasts 400/422 bodies; nothing else to say
      }
      this.uploading = false;
    },

    // ---- previews
    // Fill {{key}} tokens of a template body with sample values; seller slots show their value or the label faint
    bodyParts(body, slots) {
      const text = String(body || "");
      const out = [];
      let last = 0;
      let m;
      OWN_PARAM.lastIndex = 0;
      while ((m = OWN_PARAM.exec(text))) {
        if (m.index > last) out.push({ t: text.slice(last, m.index), kind: "" });
        out.push(this.tokenPart(m[1], slots));
        last = m.index + m[0].length;
      }
      if (last < text.length) out.push({ t: text.slice(last), kind: "" });
      return out;
    },

    tokenPart(key, slots) {
      const slot = (slots || []).find((s) => s.key === key);
      if (key === "name") return { t: SAMPLE.name, kind: "name" };
      if (key === "shop") return { t: this.shopName, kind: "name" };
      if (key === "order") return { t: SAMPLE.order, kind: "slot" };
      const value = String(this.slots[key] || "").trim();
      if (value) return { t: value, kind: "slot" };
      return { t: "{{" + ((slot && slot.labelTh) || key) + "}}", kind: "faint" };
    },

    // ---- step 5: template, draft, test, send
    requestBody() {
      const body = { groups: [this.group], pageIds: this.selectedPages.map((p) => String(p.pageId)) };
      if (this.scope === "day") {
        body.kind = "window";
        body.message = { text: this.msg.text.split(NAME_CHIP).join(NAME_TOKEN) };
        if (this.msg.imageUrl) body.message.imageUrl = this.msg.imageUrl;
        return body;
      }
      body.kind = "utility";
      const values = {};
      this.sellerSlots.forEach((s) => {
        values[s.key] = String(this.slots[s.key] || "").trim();
      });
      body.slots = values;
      if (this.itype === "own") body.templateId = this.ownTpl.id;
      else body.templateKey = this.currentTpl.key;
      return body;
    },

    async prepare() {
      const seq = ++this.seq;
      clearTimeout(this.retryTimer);
      this.createError = "";
      this.createFailed = false;
      this.refused = false;
      this.templateWait = false;
      this.draft = null;
      this.stopPoll();
      if (this.scope === "inbox") {
        if (this.itype === "own" && !(await this.ensureOwnTemplate(seq))) return;
        if (seq !== this.seq) return;
        if (!this.currentTpl) {
          await this.loadTemplates();
          if (seq !== this.seq) return;
        }
        if (!this.currentTpl) {
          this.step = 4; // the message is not there (yet): choose again
          return;
        }
        if (this.tplState === "rejected") {
          this.rejected();
          return;
        }
        if (this.tplState === "pending") {
          this.templateWait = true; // รอเฟสตรวจ: look again until เฟส decides
          this.startPoll();
          return;
        }
      }
      const body = this.requestBody();
      const sig = JSON.stringify(body);
      if (!this.keys[sig]) this.keys[sig] = newKey();
      body.idempotencyKey = this.keys[sig];
      this.creating = true;
      const { ok, data } = await dispatch(this, "createBroadcast", { body });
      if (this._isDestroyed || seq !== this.seq) return;
      this.creating = false;
      if (ok) {
        this.draft = { id: data.id, sig, audience: data.audience || { recipients: 0 }, samples: data.samples || [] };
      } else if (data.code === "rate_limited" || data.status === 429) {
        // the same key again once the API says so
        this.creating = true;
        this.retryTimer = setTimeout(() => {
          if (!this._isDestroyed && seq === this.seq) this.prepare();
        }, Math.min(Math.max(data.retryAfter || 5, 1), 60) * 1000);
      } else if (data.code === "template_pending") {
        this.templateWait = true; // pages[] names the Pages still waiting; the poll watches the selected ones
        this.startPoll();
      } else if (data.code === "template_rejected") {
        this.stopPoll();
        this.templateWait = false;
        this.createFailed = true;
        this.refused = true;
        this.createError = this.rejectText || data.messageTh || "";
      } else {
        this.createFailed = true;
        this.createError = data.messageTh || "";
      }
    },

    rejected() {
      this.templateWait = false;
      this.createFailed = true;
      this.refused = true;
      this.createError = this.rejectText;
    },

    // The seller's own text goes to เฟส once; the same text on the same Pages is not sent twice
    async ensureOwnTemplate(seq) {
      const ids = this.selectedIds;
      const lib = this.ownFromLibrary;
      const covers = (tpl) => ids.every((id) => (tpl.pages || []).some((p) => String(p.pageId) === id));
      if (lib && this.ownText === lib.body && covers(lib)) {
        this.ownTpl = lib;
        return true;
      }
      const key = this.ownText + "|" + ids.slice().sort().join(",");
      if (this.ownTpl && this.ownTpl.sourceKey === key) return true;
      this.creating = true;
      const { ok, data } = await dispatch(this, "createTemplate", { pageIds: ids, text: this.ownText });
      if (this._isDestroyed || seq !== this.seq) return false;
      this.creating = false;
      if (!ok) {
        const message = data.messageTh || this.$t("messenger.unavailable");
        bcToast(message, "danger");
        this.createFailed = true;
        this.createError = data.messageTh || "";
        return false;
      }
      this.ownTpl = Object.assign({}, data.template, { sourceKey: key });
      return true;
    },

    startPoll() {
      this.stopPoll();
      this.poll = setInterval(async () => {
        if (!(await this.loadTemplates())) return;
        if (this._isDestroyed) return;
        const mine = this.itype === "own" ? this.ownTpl : this.currentTpl;
        const fresh = mine && this.templates.find((t) => (mine.id != null ? t.id === mine.id : t.key === mine.key));
        if (!fresh) return;
        if (this.itype === "own") {
          this.ownTpl = Object.assign({}, fresh, { sourceKey: this.ownTpl.sourceKey });
          if (this.ownFromLibrary && this.ownFromLibrary.id === fresh.id) this.ownFromLibrary = this.ownTpl;
        }
        if (this.tplState === "rejected") {
          this.stopPoll();
          this.rejected();
        } else if (this.tplState === "approved") {
          this.stopPoll();
          this.prepare();
        }
      }, POLL_MS);
    },

    stopPoll() {
      if (this.poll) clearInterval(this.poll);
      this.poll = null;
    },

    async sendTest() {
      if (!this.draft || this.testing || this.sending) return;
      const phone = this.phone.trim();
      if (!this.testOpen || !phone) {
        this.testOpen = true;
        bcToast(this.$t("broadcast.screens.confirm.test_need_phone"), "danger");
        return;
      }
      this.testing = true;
      const { ok, data } = await dispatch(this, "testBroadcast", { id: this.draft.id, phone });
      this.testing = false;
      if (ok) bcToast(this.$t("broadcast.screens.confirm.test_sent"), "success");
      else bcToast(data.messageTh || this.$t("messenger.unavailable"), "danger");
    },

    async send() {
      if (!this.canSend) {
        if (this.draft && this.recipients === 0) bcToast(this.$t("broadcast.screens.confirm.no_recipients_toast"), "danger");
        return;
      }
      this.sending = true;
      const id = this.draft.id;
      const { ok, data } = await dispatch(this, "startBroadcast", { id });
      if (ok) return this.started(id);
      if (data.code === "bad_state") {
        // the first call may have gone through and only the answer was lost
        const check = await dispatch(this, "fetchBroadcast", { id });
        if (check.ok && check.data.status !== "draft") return this.started(id);
      }
      this.sending = false;
      bcToast(data.messageTh || this.$t("messenger.unavailable"), "danger");
      if (data.code === "draft_expired") {
        delete this.keys[this.draft.sig];
        this.prepare();
      }
    },

    started(id) {
      bcToast(this.$t("broadcast.screens.confirm.sent_toast"), "success");
      this.$emit("sent", id);
    },
  },
};
</script>

<style lang="scss" scoped>
.bcx-pane {
  background: #f3f3f3;
  border-radius: 10px;
  padding: 1rem;
}

.bcx-pane-head {
  font-size: 0.875rem;
  font-weight: 700;
  color: #646464;
  margin-bottom: 0.5rem;
}

.bcx-label {
  display: block;
  margin-top: 0.5rem;
  font-size: 0.875rem;
  font-weight: 700;
  color: #646464;
}

.bcx-chip {
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.5rem 0.85rem;
  border: 1px solid #d5d5d5;
  border-radius: 999px;
  background: #f1f1f1;
  color: #5f6368;
  font-family: inherit;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid $brand-blue-mid;
    outline-offset: 2px;
  }
}

.bcx-rowbtns {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;

  .btn {
    flex: 1 1 auto;
  }
}

.bcx-summary {
  background: #f3f3f3;
  border-radius: 10px;
  padding: 1rem;
  margin-bottom: 1rem;
}

.bcx-bignum {
  font-size: 1.6rem;
  font-weight: 700;
  color: #333;
  margin: 0;
}

.bcx-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin: 0.25rem 0;
  color: #333;
  font-size: 0.9375rem;

  .btn-sm {
    min-height: 44px;
    min-width: 88px;
  }
}

.bcx-muted {
  color: #666;
}

.bcx-note {
  color: #666;
  font-size: 0.875rem;
  margin-top: 0.75rem;
}

.bcx-approved {
  color: #1a6e2e;
  font-weight: 700;
  font-size: 0.875rem;
  margin: 0.35rem 0 0.6rem;
}

.bcx-pending {
  color: #9a5a00;
  font-weight: 700;
  font-size: 0.875rem;
  margin: 0.35rem 0 0.6rem;
}

.bcx-rejected {
  color: #c62828;
  font-weight: 700;
  font-size: 0.9375rem;
}

.bcx-own-warn {
  font-size: 0.875rem;
  color: #9a5a00;
  margin-bottom: 0.5rem;
}

.bcx-sticky-n {
  font-weight: 700;
  font-size: 1.05rem;
  color: #333;
  white-space: nowrap;
}

textarea.form-control {
  min-height: 0;
}

.bcx-count {
  text-align: right;
  font-size: 0.8125rem;
  color: #888;

  &.--bad {
    color: #c62828;
    font-weight: 700;
  }
}

body.--dark {
  .bcx-count {
    color: #b5b5b5;

    &.--bad {
      color: #ff9d9d;
    }
  }
  .bcx-pane,
  .bcx-summary {
    background: #4b4b4b;
  }

  .bcx-pane-head,
  .bcx-label,
  .bcx-note,
  .bcx-muted {
    color: #c4c4c4;
  }

  .bcx-bignum,
  .bcx-line,
  .bcx-sticky-n {
    color: #e6e6e6;
  }

  .bcx-chip {
    background: #4b4b4b;
    border-color: #666;
    color: #e0e0e0;
  }

  .bcx-approved {
    color: #8fe3a0;
  }

  .bcx-pending {
    color: #ffd98a;
  }

  .bcx-rejected {
    color: #ff9d9d;
  }

  .bcx-own-warn {
    color: #ffd98a;
  }
}
</style>

